import { canNavigate, ConductorOneChildActive, Router, ScreenBase, splitUrlSegments } from "@frui.ts/screens";
import LocalizationService from "services/localizationService";
import SingleBankTransferViewModel, { SingleBankTransferNavigationName } from "../common/payment/singleBankTransferViewModel";
import SingleStatusViewModel, { SingleStatusViewNavigationName } from "../common/payment/singleStatusViewModel";
import StatusViewModel, { StatusViewNavigationName } from "../common/payment/statusViewModel";
import NotificationService, { NotificationType } from "services/notificationService";
import { IconType } from "@edaz/edazkit/Icon";
import { action, computed, reaction, when } from "mobx";
import { clearSearchParameters } from "utils/browserHistory";
import { isSafari } from "react-device-detect";
import OutageService from "services/outageService";
import OrderOutageViewModel from "../common/orderOutage/orderOutageViewModel";
import OrderWizardViewModel, { OrderNewViewNavigationName } from "./order/orderWizardModel";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import LicenseViewModel, { LicenseViewNavigationName } from "./order/steps/licenseViewModel";
import { BankTransferNavigationName } from "viewModels/common/payment/bankTransferViewModel";
import TimeService from "services/timeService";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import EventBus, { currentServerTime } from "services/eventBus";
import PaymentResult from "models/paymentResult";
import PaymentRetryViewModel, {
  clearRetryConsumed,
  isRetryBlocked,
  markRetryConsumed,
  PaymentRetryNavigationName,
} from "../common/payment/paymentRetryViewModel";
import SinglePaymentRetryViewModel from "../common/payment/singlePaymentRetryViewModel";
import PaymentService from "services/payment/paymentService";
import { PayGatePaymentStatus } from "models/payGatePaymentResponse";

export const EshopModuleNavigationName = "eshop";

@Router.registerRoute({
  route: EshopModuleNavigationName,
  name: Router.Self,
  children: [
    OrderWizardViewModel,
    SingleBankTransferViewModel,
    SingleStatusViewModel,
    OrderOutageViewModel,
    SinglePaymentRetryViewModel,
  ],
})
export default class EshopViewModel extends ConductorOneChildActive<ScreenBase> {
  private unsubsriptions: Array<() => void> = [];
  OFFLINE_NOTIFICATION_KEY = "OFFLINE";

  @computed
  get outage() {
    return this.outageService.eshopActiveOutage;
  }

  constructor(
    public localizationService: LocalizationService,
    private orderWizardModel: OrderWizardViewModel,
    public timeService: TimeService,
    private eventBus: EventBus,
    private outageService: OutageService,
    public repository: ChargeOrderRepository,
    private bankTransferVMFactory: ReturnType<typeof SingleBankTransferViewModel.Factory>,
    private statusVMFactory: ReturnType<typeof SingleStatusViewModel.Factory>,
    private orderOutageViewModelFactory: ReturnType<typeof OrderOutageViewModel.Factory>,
    private notificationService: NotificationService,
    private paymentRetryVMFactory: ReturnType<typeof PaymentRetryViewModel.Factory>,
    private paymentService: PaymentService
  ) {
    super();
    this.navigationName = EshopModuleNavigationName;
    this.nameValue = "eshop.title";
    this.children.push(this.orderWizardModel);
    if (history.scrollRestoration) {
      history.scrollRestoration = "manual";
    }

    if (isSafari) {
      window.onpageshow = function (event: { persisted: boolean }) {
        if (event.persisted) {
          window.location.reload();
        }
      };
    }
  }

  async onActivate() {
    window.addEventListener("online", this.hideOfflineNotification);
    window.addEventListener("offline", this.showOfflineNotification);
    const serverTime = await this.timeService.getCurrentServerTime(true);
    this.eventBus.publish(currentServerTime({ date: serverTime }));
    this.unsubsriptions.push(reaction(() => this.outage, this.onEshopOutageChanged));
    this.lazyCheckEshopOutage();

    if (!this.activeChild) {
      if (!window.location.hash.includes(`/${PaymentRetryNavigationName}/`)) {
        await this.tryActivateChild(this.orderWizardModel);
      } else {
      }
    }

    await super.onActivate();
  }

  protected async lazyCheckEshopOutage() {
    await when(() => this.outageService.isInitialized === true);
    if (this.outage && this.activeChild === this.orderWizardModel) {
      this.tryActivateChild(this.orderOutageViewModelFactory(this.outage));
    }
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    window.removeEventListener("online", this.hideOfflineNotification);
    window.removeEventListener("offline", this.showOfflineNotification);
    return super.onDeactivate(isClosing);
  }

  @action.bound
  private onEshopOutageChanged() {
    if (this.outage === undefined) {
      this.tryActivateChild(this.orderWizardModel);
    }
  }

  @action.bound
  protected async changeActiveChild(newChild: ScreenBase | undefined, closePrevious: boolean): Promise<any> {
    const leavingStatus = this.activeChild instanceof StatusViewModel || this.activeChild instanceof SingleStatusViewModel;
    if (leavingStatus && newChild instanceof OrderWizardViewModel) {
      this.orderWizardModel.resetOrder();
    }
    super.changeActiveChild(newChild, closePrevious);
  }

  @action.bound
  private hideOfflineNotification() {
    this.notificationService.remove(this.OFFLINE_NOTIFICATION_KEY);
  }

  @action.bound
  private showOfflineNotification() {
    this.notificationService.add(
      {
        title: this.localizationService.translateGeneral("notifications.offline"),
        type: NotificationType.ERROR,
        icon: IconType.Offline,
        permanent: true,
      },
      this.OFFLINE_NOTIFICATION_KEY
    );
  }

  async navigate(subPath: string | undefined, params: any) {
    const segments = splitUrlSegments(subPath);

    if (segments.length > 1 && segments[0] === PaymentRetryNavigationName && segments[1]) {
      clearRetryConsumed(segments[1]);
    }
    if (segments.length > 1 && segments[0] === PaymentRetryNavigationName && segments[1] && isRetryBlocked(segments[1])) {
      await this.tryActivateChild(this.orderWizardModel);
      return;
    }

    let childToActivate;
    if (segments.length > 0 && (segments[0] === SingleBankTransferNavigationName || segments[0] === BankTransferNavigationName)) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments.length > 1 && segments[0] === PaymentRetryNavigationName) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments.length > 1 && segments[0] === SingleStatusViewNavigationName) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments.length > 1 && segments[0] === StatusViewNavigationName && segments[1]) {
      const storedOrder = await this.orderWizardModel.chargeOrderService.getStoredChargeOrder(segments[1]);
      if (storedOrder) {
        storedOrder._origin = "single";
        this.orderWizardModel.chargeOrderService.chargeOrder = storedOrder;
      }
      const order = storedOrder ?? this.orderWizardModel.chargeOrder;

      const result = params.result;
      const failureType = params["failure_type"];
      const failureOrigin = params["failure_origin"];

      if (order) {
        clearSearchParameters();
        await this.orderWizardModel.chargeOrderService.updateChargeOrderStatus(result, order, segments[1]);
      }

      if (order?.id) {
        const retryChild = await this.tryCreatePaymentRetryChild(order);
        if (retryChild) {
          childToActivate = retryChild;
        } else {
          this.orderWizardModel.paymentResultFailureType = failureType;
          this.orderWizardModel.paymentResultFailureOrigin = failureOrigin;
          childToActivate = await this.findNavigationChild(subPath || "");
        }
      } else {
        this.orderWizardModel.paymentResultFailureType = failureType;
        this.orderWizardModel.paymentResultFailureOrigin = failureOrigin;
        childToActivate = await this.findNavigationChild(subPath || "");
      }
    } else {
      childToActivate = await this.findNavigationChild(segments[0] || "");
    }
    if (
      segments[1] &&
      segments[1] !== LicenseViewNavigationName &&
      this.orderWizardModel.activeChild instanceof LicenseViewModel
    ) {
      const isValid = this.orderWizardModel.activeChild.validateAll();

      if (!isValid) return;
    }

    if (childToActivate) {
      await this.tryActivateChild(childToActivate);

      if (canNavigate(childToActivate)) {
        await childToActivate.navigate(segments[1], params);
      }
    }
    await this.onChildNavigated(childToActivate);
  }

  @action.bound
  async getOrderStatus(id: string) {
    return (await this.repository.get(id))?.status;
  }

  @action.bound
  private async tryCreatePaymentRetryChild(chargeOrder: ChargeOrder): Promise<ScreenBase | undefined> {
    if (chargeOrder._status !== ChargeOrderStatus.Failed) return undefined;
    try {
      const payGatePayment = await this.repository.getPayGatePayment(chargeOrder.id);

      if (payGatePayment?.permittedActions?.paymentRetry?.isAllowed) {
        return this.buildPaymentRetryChild(chargeOrder, payGatePayment);
      }

      // If payment is still in progress, show existing deferred screen
      if (payGatePayment?.status === PayGatePaymentStatus.IN_PROGRESS) {
        chargeOrder._status = ChargeOrderStatus.Deferred;
        this.orderWizardModel.chargeOrderService.persistStoredChargeOrder(chargeOrder);
        return this.statusVMFactory(chargeOrder);
      }
    } catch {
      // If endpoint fails, fall back to existing behavior
    }
    return undefined;
  }

  private buildPaymentRetryChild(chargeOrder: ChargeOrder, payGatePayment?: any) {
    clearRetryConsumed(chargeOrder.id);
    const retryVM = this.paymentRetryVMFactory(
      chargeOrder,
      () => this.handleRetryPayment(chargeOrder),
      () => this.handleBackToPurchaseWithPrefill(),
      () => this.handlePaymentSucceeded(chargeOrder),
      () => this.handlePaymentInProgress(chargeOrder),
      paymentData => this.handleRetryGooglePayPayment(chargeOrder, paymentData),
      () => this.handleRetryIdleTimeout(chargeOrder)
    );
    if (payGatePayment) retryVM.payGatePaymentResponse = payGatePayment;
    return retryVM;
  }

  @action.bound
  private handleRetryIdleTimeout(chargeOrder: ChargeOrder) {
    markRetryConsumed(chargeOrder.id);
    this.orderWizardModel.chargeOrderService.reset();
    window.location.href = this.localizationService.translateGeneral("eshop.idle.timeoutRedirect", undefined, true);
  }

  @action.bound
  private async handleRetryGooglePayPayment(chargeOrder: ChargeOrder, paymentData: google.payments.api.PaymentData) {
    try {
      await this.paymentService.submitGooglePayment(chargeOrder.id, paymentData);
    } catch {
      // submit failure events are handled by retry VM's own subscribers.
    }
  }

  @action.bound
  private async handleRetryPayment(chargeOrder: ChargeOrder) {
    await this.paymentService.payGateRedirect(chargeOrder.id, EshopModuleNavigationName);
  }

  @action.bound
  private async handlePaymentSucceeded(chargeOrder: ChargeOrder) {
    markRetryConsumed(chargeOrder.id);
    await this.orderWizardModel.chargeOrderService.updateChargeOrderStatus(PaymentResult.SUCCESS, chargeOrder, chargeOrder.id);
    await this.tryActivateChild(this.statusVMFactory(chargeOrder));
  }

  @action.bound
  private async handlePaymentInProgress(chargeOrder: ChargeOrder) {
    markRetryConsumed(chargeOrder.id);
    chargeOrder._status = ChargeOrderStatus.Deferred;
    this.orderWizardModel.chargeOrderService.persistStoredChargeOrder(chargeOrder);
    await this.tryActivateChild(this.statusVMFactory(chargeOrder));
  }

  @action.bound
  private handleBackToPurchaseWithPrefill() {
    // Reopen the order (preserves data, sets status to Open)
    this.orderWizardModel.chargeOrderService.reopenOrder();
    this.orderWizardModel.initChildrenFromExistingOrder();
    this.tryActivateChild(this.orderWizardModel);
  }

  async findNavigationChild(navigationName: string) {
    const retrySegments = splitUrlSegments(navigationName);
    if (retrySegments[0] === PaymentRetryNavigationName && retrySegments.length > 1 && retrySegments[1]) {
      const orderId = retrySegments[1];
      if (isRetryBlocked(orderId)) {
        return super.findNavigationChild(OrderNewViewNavigationName);
      }
      let chargeOrder = this.orderWizardModel.chargeOrder;
      if (!chargeOrder || chargeOrder.id !== orderId) {
        const stored = await this.orderWizardModel.chargeOrderService.getStoredChargeOrder(orderId);
        if (stored) {
          stored._origin = "single";
          this.orderWizardModel.chargeOrderService.chargeOrder = stored;
          chargeOrder = stored;
        }
      }
      if (chargeOrder?.id) {
        return this.buildPaymentRetryChild(chargeOrder);
      }
    }
    if (navigationName.includes(SingleBankTransferNavigationName) || navigationName.includes(BankTransferNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments.length > 1 && segments[1]) {
        const chargeOrder = this.orderWizardModel.chargeOrder;
        if (chargeOrder && chargeOrder._status === ChargeOrderStatus.PaidBankTransfer) {
          this.orderWizardModel.chargeOrderService.persistStoredChargeOrder(chargeOrder);
          return this.bankTransferVMFactory(chargeOrder);
        }
      }
    } else if (navigationName.includes(StatusViewNavigationName) || navigationName.includes(SingleStatusViewNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments.length > 1 && segments[1]) {
        const orderId = segments[1];
        let chargeOrder = this.orderWizardModel.chargeOrder;

        if (!chargeOrder || chargeOrder.id !== orderId) {
          const storedOrder = await this.orderWizardModel.chargeOrderService.getStoredChargeOrder(orderId);
          if (storedOrder) {
            storedOrder._origin = "single";
            this.orderWizardModel.chargeOrderService.chargeOrder = storedOrder;
            chargeOrder = storedOrder;
          }
        }

        let beStatus: ChargeOrderStatus | undefined;
        try {
          const remoteOrder = await this.repository.get(orderId);
          beStatus = remoteOrder?.status;
          if (remoteOrder && (!chargeOrder || chargeOrder.id !== orderId)) {
            await this.orderWizardModel.chargeOrderService.updateOrder(remoteOrder);
            chargeOrder = this.orderWizardModel.chargeOrderService.chargeOrder;
          }
        } catch {
          beStatus = undefined;
        }

        // Local status was just set by navigate() based on the URL `result` query param
        // (e.g. result=success → Paid). Trust it as a fallback when BE hasn't caught up
        // yet (race after second-tab payment_confirmation roundtrip).
        const localStatus = chargeOrder?._status;
        const isFinalStatus = (s?: ChargeOrderStatus) => s === ChargeOrderStatus.Paid || s === ChargeOrderStatus.Deferred;

        if (chargeOrder && (isFinalStatus(beStatus) || isFinalStatus(localStatus))) {
          const finalStatus = isFinalStatus(beStatus) ? beStatus! : localStatus!;
          chargeOrder._status = finalStatus;
          this.orderWizardModel.chargeOrderService.persistStoredChargeOrder(chargeOrder);
          return this.statusVMFactory(chargeOrder);
        } else {
          return super.findNavigationChild(OrderNewViewNavigationName);
        }
      }
    } else if (navigationName === OrderNewViewNavigationName && this.outage) {
      return this.orderOutageViewModelFactory(this.outage);
    }
    return super.findNavigationChild(navigationName);
  }
}
